import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/contexts/AuthContext'
import { Button } from '@/components/ui/button'
import { isGuestMode, isInIframe } from '@/utils/iframeDetector'
import { useLocalizedNavigate } from '@/hooks/useLocalizedNavigate'
import LanguageToggle from '@/components/LanguageToggle'

const Header: React.FC = () => {
  const { t } = useTranslation('common')
  const { user, signOut } = useAuth()
  const navigate = useLocalizedNavigate()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const isGuest = isGuestMode()

  const handleSignOut = async () => {
    try {
      await signOut()
      setIsMenuOpen(false)
    } catch (error) {
      console.error('Error signing out:', error)
    }
  }

  const handleNewPrompt = () => {
    navigate('/')
    setIsMenuOpen(false)
  }

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false)
      }
    }

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isMenuOpen])

  return (
    <header className="bg-white shadow-sm border-b">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex flex-col">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{t('header.title')}</h1>
            <span className="text-xs sm:text-sm text-gray-600">{t('header.subtitle')}</span>
          </div>

          <div className="flex items-center space-x-2 sm:space-x-4">
            {!isInIframe() && <LanguageToggle />}

            {user && !isGuest && (
              <div className="hidden sm:flex items-center space-x-3">
                <span className="text-sm text-gray-600 truncate max-w-[120px] lg:max-w-none">
                  {user.email}
                </span>
                <Button
                  onClick={handleNewPrompt}
                  variant="outline"
                  size="sm"
                  className="hidden md:inline-flex"
                >
                  {t('header.newPrompt')}
                </Button>
              </div>
            )}

            {/* Show New Prompt button in guest mode (iframe) */}
            {isGuest && (
              <Button
                onClick={handleNewPrompt}
                variant="outline"
                size="sm"
                className="text-sm"
              >
                {t('header.newPrompt')}
              </Button>
            )}
            
            {/* Hide hamburger menu in guest mode */}
            {!isGuest && (
            <div className="relative" ref={menuRef}>
              <button 
                className="p-2"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
              >
                <div className="w-6 h-6 flex flex-col justify-center">
                  <div className="w-6 h-1 bg-gray-600 mb-1"></div>
                  <div className="w-6 h-1 bg-gray-600 mb-1"></div>
                  <div className="w-6 h-1 bg-gray-600"></div>
                </div>
              </button>
              
              {isMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border z-50">
                  <div className="py-1">
                      {user && (
                        <button
                          onClick={handleNewPrompt}
                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 sm:hidden"
                        >
                          {t('header.newPrompt')}
                        </button>
                      )}
                      {user && (
                        <div className="px-4 py-2 text-xs text-gray-500 sm:hidden border-b border-gray-100">
                          {user.email}
                        </div>
                      )}
                      {user && (
                    <button
                      onClick={handleSignOut}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      {t('header.signOut')}
                    </button>
                      )}
                    </div>
                  </div>
                )}
                </div>
              )}
          </div>
        </div>
      </div>
    </header>
  )
}

export default Header
